'use client';

import { useState, useEffect, useRef } from 'react';
import { Bell, CheckCheck, ChevronRight, RefreshCw } from 'lucide-react';
import Link from 'next/link';

interface NotificationItem {
  doc_num: string;
  doc_date: string;
  name: string;
  netamt: number;
  type: string;
  typeLabel: string;
  link: string;
  colorClass: string;
}

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [readDocs, setReadDocs] = useState<string[]>([]);
  const [filterType, setFilterType] = useState<string>('ALL');
  const [refreshing, setRefreshing] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Load read state from localStorage
    const savedRead = localStorage.getItem('read_notifications');
    if (savedRead) {
      try {
        setReadDocs(JSON.parse(savedRead));
      } catch (e) {}
    }

    fetchNotifications();

    // Auto refresh every 20 seconds
    const timer = setInterval(() => {
      fetchNotifications();
    }, 20000);

    return () => clearInterval(timer);
  }, []);

  // Re-fetch whenever dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      setRefreshing(true);
      const activeDb = localStorage.getItem('active_company') || 'express_db';
      const userStr = localStorage.getItem('user');
      let uName = '';
      let uUser = '';
      let uRole = '';
      let uPerms: string[] = [];
      if (userStr) {
        try {
          const u = JSON.parse(userStr);
          uName = u.name || u.description || '';
          uUser = u.username || '';
          uRole = u.role || '';
          if (typeof u.permissions === 'string') {
            try { uPerms = JSON.parse(u.permissions); } catch(e){}
          } else if (Array.isArray(u.permissions)) {
            uPerms = u.permissions;
          }
        } catch(e){}
      }
      const res = await fetch(`/api/notifications?db=${activeDb}&user=${encodeURIComponent(uUser)}&name=${encodeURIComponent(uName)}&role=${encodeURIComponent(uRole)}&perms=${encodeURIComponent(JSON.stringify(uPerms))}&t=${Date.now()}`).catch(() => null);
      if (!res || !res.ok) return;
      const text = await res.text().catch(() => '');
      try {
        const data = JSON.parse(text);
        if (data.success) {
          setNotifications(data.notifications || []);
        }
      } catch(jsonErr) {}
    } catch (e) {
      // Ignore network errors silently
    } finally {
      setRefreshing(false);
    }
  };

  const markAllAsRead = () => {
    const allIds = notifications.map(n => n.doc_num);
    setReadDocs(allIds);
    localStorage.setItem('read_notifications', JSON.stringify(allIds));
  };

  const markAsRead = (docNum: string) => {
    if (!readDocs.includes(docNum)) {
      const updated = [...readDocs, docNum];
      setReadDocs(updated);
      localStorage.setItem('read_notifications', JSON.stringify(updated));
    }
  };

  const unreadCount = notifications.filter(n => !readDocs.includes(n.doc_num)).length;

  const filtered = filterType === 'ALL' 
    ? notifications 
    : filterType === 'EXPENSE'
    ? notifications.filter(n => n.type === 'OE' || n.type === 'OX')
    : notifications.filter(n => n.type === filterType);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:bg-slate-800 hover:border-slate-700 transition shadow-lg flex items-center justify-center group"
        title="การแจ้งเตือนบันทึกเอกสารใหม่ทุกประเภท"
      >
        <Bell size={20} className="group-hover:rotate-12 transition transform duration-200 text-slate-300 group-hover:text-amber-400" />
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 flex h-5 min-w-[20px] px-1 items-center justify-center rounded-full bg-rose-600 text-[11px] font-bold text-white shadow-md shadow-rose-900/50 animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Popup */}
      {isOpen && (
        <div className="absolute right-0 mt-3 w-80 sm:w-96 bg-slate-900/95 backdrop-blur-2xl border border-slate-800 rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
          {/* Header */}
          <div className="p-4 border-b border-slate-800/80 flex items-center justify-between bg-slate-950/60">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400">
                <Bell size={18} />
              </div>
              <div>
                <h3 className="font-bold text-white text-sm">แจ้งเตือนบันทึกทุกประเภท</h3>
                <p className="text-[11px] text-slate-400">PO, SO, QU, IV, RR, ค่าใช้จ่าย, โอนเงิน</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={fetchNotifications}
                className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition"
                title="รีเฟรชการแจ้งเตือน"
              >
                <RefreshCw size={14} className={refreshing ? 'animate-spin text-blue-400' : ''} />
              </button>

              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-[11px] text-blue-400 hover:text-blue-300 flex items-center gap-1 bg-blue-500/10 hover:bg-blue-500/20 px-2 py-1 rounded-lg border border-blue-500/20 transition"
                >
                  <CheckCheck size={13} /> อ่านหมดแล้ว
                </button>
              )}
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-1 p-2 bg-slate-950/40 border-b border-slate-800/60 overflow-x-auto text-[11px] scrollbar-none">
            {[
              { id: 'ALL', label: 'ทั้งหมด' },
              { id: 'PO', label: 'PO' },
              { id: 'SO', label: 'SO' },
              { id: 'QU', label: 'QU' },
              { id: 'IV', label: 'IV' },
              { id: 'RR', label: 'RR' },
              { id: 'EXPENSE', label: 'ค่าใช้จ่าย' },
              { id: 'TR', label: 'โอนเงิน' },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setFilterType(t.id)}
                className={`px-2 py-1 rounded-md font-semibold transition shrink-0 ${
                  filterType === t.id 
                    ? 'bg-blue-600 text-white shadow-sm' 
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-800/50">
            {filtered.length === 0 ? (
              <div className="py-10 text-center text-slate-500 text-xs">
                ไม่มีรายการบันทึกเอกสารในหมวดหมู่นี้
              </div>
            ) : (
              filtered.map((item, idx) => {
                const isRead = readDocs.includes(item.doc_num);
                return (
                  <Link
                    key={`${item.type}-${item.doc_num}-${idx}`}
                    href={item.link}
                    onClick={() => {
                      markAsRead(item.doc_num);
                      setIsOpen(false);
                    }}
                    className={`p-3.5 flex items-start gap-3 transition hover:bg-slate-800/60 block relative group ${
                      !isRead ? 'bg-blue-950/20' : ''
                    } ${(item.approval_status === 'approved' || item.approval_status === 'APPROVED') ? 'bg-emerald-950/20' : ''}`}
                  >
                    {!isRead && (
                      <span className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                    )}

                    <div className={`px-2 py-0.5 rounded text-[10px] font-bold border shrink-0 mt-0.5 flex flex-col items-center justify-center gap-1 ${(item.approval_status === 'approved' || item.approval_status === 'APPROVED') ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' : item.colorClass}`}>
                      <span>{item.type}</span>
                      {(item.approval_status === 'approved' || item.approval_status === 'APPROVED') && <span className="text-[8px] bg-emerald-500 text-slate-900 px-1 rounded-sm leading-none py-0.5 mt-0.5">อนุมัติแล้ว</span>}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1 mb-0.5">
                        <span className="font-bold text-slate-200 text-xs truncate group-hover:text-blue-400 transition">
                          {item.doc_num}
                        </span>
                        <span className="text-[10px] text-slate-400 shrink-0">
                          {formatDate(item.doc_date)}
                        </span>
                      </div>

                      <p className="text-[11px] text-slate-300 truncate mb-1">
                        {item.name}
                      </p>

                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-slate-400 text-[10px]">{item.typeLabel}</span>
                        <span className="font-bold text-emerald-400">
                          ฿{item.netamt.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="p-2.5 bg-slate-950/80 border-t border-slate-800/80 text-center">
            <Link
              href="/approvals"
              onClick={() => setIsOpen(false)}
              className="text-[11px] text-slate-400 hover:text-white flex items-center justify-center gap-1 py-1 transition"
            >
              ดูหน้าอนุมัติเอกสารทั้งหมด <ChevronRight size={13} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
